import api from './api'
import type { Conference } from '../types'

/**
 * Admin API for folding duplicate competitor identities together.
 *
 * @remarks
 * Kept apart from `services/league.ts` because merging is the one admin action
 * that destroys a row. Its types carry the evidence the owner needs in order to
 * authorise that — game counts, seasons, Discord handle, and every reason the
 * server might refuse — and none of it is useful to any other page.
 */

/** Why the server will not perform a merge. */
export type MergeBlockerCode =
  | 'same-player'
  | 'missing-player'
  | 'both-linked'
  | 'shared-game'
  | 'unresolved-conference'

export interface MergeBlocker {
  code: MergeBlockerCode
  message: string
}

export interface MergeSide {
  id: number
  /** What the site renders: the league override if set, otherwise Discord. */
  displayName: string
  leagueDisplayName: string | null
  /** The underlying Discord-tracked name, shown so two rows never look alike. */
  discordDisplayName: string
  userId: number | null
  discordId: string | null
  discordUsername: string | null
  discordAvatar: string | null
  linked: boolean
  games: number
  wins: number
  gameSeasons: number[]
  rosterSeasons: number[]
  titleSeasons: number[]
  aliases: string[]
}

export type SeasonPlanAction = 'move' | 'keep-target' | 'conflict'

export interface SeasonPlanEntry {
  seasonNumber: number
  seasonId: number
  action: SeasonPlanAction
  sourceConference: Conference
  targetConference: Conference | null
}

export interface SharedGame {
  gameId: number
  seasonNumber: number
  homeScore: number
  awayScore: number
  status: string
}

export interface MergePreview {
  source: MergeSide
  target: MergeSide
  blockers: MergeBlocker[]
  canMerge: boolean
  sharedGames: SharedGame[]
  gameParticipantsMoving: number
  winnerRowsMoving: number
  championRowsMoving: number
  runnerUpRowsMoving: number
  aliasesMoving: string[]
  aliasesToRecord: string[]
  seasonPlan: SeasonPlanEntry[]
  conferenceConflicts: SeasonPlanEntry[]
}

export interface ConferenceResolution {
  seasonNumber: number
  conference: Conference
}

export interface MergeAuditEntry {
  id: number
  sourceLeaguePlayerId: number
  sourceDisplayName: string
  sourceLeagueDisplayName: string | null
  targetLeaguePlayerId: number | null
  targetDisplayName: string
  gameParticipantsMoved: number
  membershipsMoved: number
  membershipsDropped: number
  winnerRowsMoved: number
  championRowsMoved: number
  runnerUpRowsMoved: number
  aliasesMoved: number
  aliasesRecorded: string[]
  conferenceResolutions: ConferenceResolution[]
  performedBy: string | null
  createdAt: string
}

export interface NameSimilarity {
  /** 0–1; 1 means the two spellings differ only in case or punctuation. */
  score: number
  reason: string
}

export interface ReconciliationPlayer {
  id: number
  displayName: string
  leagueDisplayName: string | null
  discordDisplayName: string
  linked: boolean
  discordId: string | null
  discordAvatar: string | null
  games: number
  seasons: number[]
  gameSeasons: number[]
}

export interface MergeCandidate extends ReconciliationPlayer {
  similarity: NameSimilarity
  /** The pair faced each other, so merging them is impossible. */
  blockedBySharedGame: boolean
}

export interface MergeCandidateGroup {
  source: ReconciliationPlayer
  candidates: MergeCandidate[]
}

/** Ranked duplicate proposals for the whole roster. Never auto-applied. */
export async function fetchMergeCandidates(): Promise<MergeCandidateGroup[]> {
  const { data } = await api.get<MergeCandidateGroup[]>('/admin/players/merge-candidates')
  return data
}

/**
 * Computes what a merge would do without writing anything.
 *
 * @remarks
 * Resolves even when the merge is refused: `canMerge` is false and `blockers`
 * explains why, which is more useful to show than an error toast.
 */
export async function previewPlayerMerge(
  sourceId: number,
  targetId: number
): Promise<MergePreview> {
  const { data } = await api.post<MergePreview>('/admin/players/merge/preview', {
    sourceId,
    targetId,
  })
  return data
}

/**
 * Performs a merge the admin has confirmed. Irreversible.
 *
 * @param conferenceResolutions - One entry per season in the preview's
 * `conferenceConflicts`; the server refuses the merge without them.
 * @returns The audit row, which doubles as the result summary.
 */
export async function applyPlayerMerge(
  sourceId: number,
  targetId: number,
  conferenceResolutions: ConferenceResolution[] = []
): Promise<MergeAuditEntry> {
  const { data } = await api.post<MergeAuditEntry>('/admin/players/merge', {
    sourceId,
    targetId,
    conferenceResolutions,
  })
  return data
}

/** Every merge already performed, newest first. */
export async function fetchMergeHistory(): Promise<MergeAuditEntry[]> {
  const { data } = await api.get<MergeAuditEntry[]>('/admin/players/merges')
  return data
}
